import { useState } from "react";
import { Modal } from "react-bootstrap";
import { submitDamageReport } from "@services/krent.service";

interface Props {
  transactionId: string;
  cautionFee: number;
  onClose: () => void;
  onReported: () => void;
}

const MAX_PHOTOS = 10;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function DamageReportModal({
  transactionId,
  cautionFee,
  onClose,
  onReported,
}: Props) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [reported, setReported] = useState(false);

  function pickPhotos(files: FileList | null) {
    const picked = Array.from(files || []);
    if (picked.some((file) => !ACCEPTED_TYPES.includes(file.type))) {
      setError("Photos must be JPG, PNG or WebP.");
      return;
    }
    if (picked.length > MAX_PHOTOS) {
      setError(`You can add up to ${MAX_PHOTOS} photos.`);
      return;
    }
    setError("");
    setPhotos(picked);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const amountClaimed = Number(amount);
    if (!description.trim()) return setError("Describe what was damaged.");
    if (!amountClaimed || amountClaimed <= 0) return setError("Enter the amount you are claiming.");
    if (amountClaimed > cautionFee) {
      return setError(`You can claim up to the caution fee of ₦${cautionFee.toLocaleString("en-NG")}.`);
    }

    const form = new FormData();
    form.append("description", description.trim());
    form.append("amountClaimed", String(amountClaimed));
    photos.forEach((photo) => form.append("photos", photo));

    setSubmitting(true);
    setError("");
    try {
      await submitDamageReport(transactionId, form);
      setReported(true);
      onReported();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to submit the damage report.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal show centered onHide={() => !submitting && onClose()} backdrop={submitting ? "static" : true}>
      <Modal.Body className="p-4">
        <h2 className="fw-bold mb-2" style={{ fontSize: 20 }}>
          Report damage
        </h2>
        {reported ? (
          <>
            <p className="mb-3" style={{ fontSize: 14, color: "#4b5563" }} role="status">
              Thanks. Krent will inspect your report before the caution fee is refunded and let you know the outcome.
            </p>
            <button type="button" className="btn w-100 text-white" style={{ backgroundColor: "#47BBA9" }} onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="mb-3" style={{ fontSize: 14, color: "#4b5563" }}>
              Tell us what was damaged and how much it will cost to fix. You can claim up to the caution fee of ₦
              {cautionFee.toLocaleString("en-NG")}.
            </p>
            <label htmlFor="damage-description" className="form-label fw-semibold" style={{ fontSize: 13 }}>
              What was damaged?
            </label>
            <textarea
              id="damage-description"
              className="form-control mb-3"
              rows={4}
              value={description}
              disabled={submitting}
              onChange={(event) => {
                setDescription(event.target.value);
                setError("");
              }}
            />
            <label htmlFor="damage-amount" className="form-label fw-semibold" style={{ fontSize: 13 }}>
              Amount to claim (₦)
            </label>
            <input
              id="damage-amount"
              type="number"
              inputMode="decimal"
              min={1}
              max={cautionFee}
              className="form-control mb-3"
              value={amount}
              disabled={submitting}
              onChange={(event) => {
                setAmount(event.target.value);
                setError("");
              }}
            />
            <label htmlFor="damage-photos" className="form-label fw-semibold" style={{ fontSize: 13 }}>
              Photos (up to {MAX_PHOTOS})
            </label>
            <input
              id="damage-photos"
              type="file"
              accept={ACCEPTED_TYPES.join(",")}
              multiple
              className="form-control mb-1"
              disabled={submitting}
              onChange={(event) => pickPhotos(event.target.files)}
            />
            {photos.length > 0 && (
              <p className="mb-0" style={{ fontSize: 12, color: "#6b7280" }}>
                {photos.length} photo{photos.length === 1 ? "" : "s"} selected
              </p>
            )}
            {error && (
              <p className="mt-2 mb-0" style={{ fontSize: 13, color: "#dc2626" }} role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="btn w-100 mt-3 text-white"
              style={{ backgroundColor: "#47BBA9" }}
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit report"}
            </button>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
}
